import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Mail, Lock, User, Building2 } from 'lucide-react';

import digisendIcon from '@/assets/digisend-icon.png';

interface CompanyBranding {
  name: string;
  trade_name: string | null;
  logo_url: string | null;
}

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<CompanyBranding | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch company branding for login page
  useEffect(() => {
    const fetchCompany = async () => {
      const { data } = await supabase
        .from('companies')
        .select('name, trade_name, logo_url')
        .limit(1)
        .single();
      
      if (data) {
        setCompany(data);
      }
    };
    fetchCompany();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: 'Login realizado!',
        description: 'Você será redirecionado para o dashboard.',
      });
      
      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: 'Erro no login',
        description: error.message || 'Verifique suas credenciais.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      toast({
        title: 'Conta criada!',
        description: 'Verifique seu email para confirmar o cadastro.',
      });
    } catch (error: any) {
      toast({
        title: 'Erro no cadastro',
        description: error.message || 'Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-accent/5 p-4 relative overflow-hidden">
      {/* Background Digisend Icons */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img 
          src={digisendIcon} 
          alt="" 
          className="absolute top-[10%] left-[5%] w-16 h-16 opacity-[0.04] rotate-[-15deg]"
        />
        <img 
          src={digisendIcon} 
          alt="" 
          className="absolute top-[20%] right-[8%] w-24 h-24 opacity-[0.03] rotate-[20deg]"
        />
        <img 
          src={digisendIcon} 
          alt="" 
          className="absolute bottom-[25%] left-[10%] w-20 h-20 opacity-[0.04] rotate-[10deg]"
        />
        <img 
          src={digisendIcon} 
          alt="" 
          className="absolute bottom-[15%] right-[5%] w-28 h-28 opacity-[0.03] rotate-[-10deg]"
        />
        <img 
          src={digisendIcon} 
          alt="" 
          className="absolute top-[50%] left-[3%] w-12 h-12 opacity-[0.03] rotate-[30deg]"
        />
        <img 
          src={digisendIcon} 
          alt="" 
          className="absolute top-[5%] right-[30%] w-14 h-14 opacity-[0.04] rotate-[-25deg]"
        />
      </div>

      <Card className="w-full max-w-md shadow-xl relative z-10">
        {/* Company Branding Header */}
        <div className="flex items-center justify-center gap-4 p-6 pb-2">
          {company?.logo_url ? (
            <img 
              src={company.logo_url} 
              alt={company.trade_name || company.name} 
              className="h-14 w-14 object-contain rounded-xl"
            />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-[hsl(var(--basic-100))] flex items-center justify-center">
              <Building2 className="h-7 w-7 text-[hsl(var(--basic-500))]" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-[hsl(var(--basic-900))]">
            {company?.trade_name || company?.name || 'Carregando...'}
          </h1>
        </div>

        <CardContent className="pt-4">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Entrar</TabsTrigger>
              <TabsTrigger value="signup">Criar Conta</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nome Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Seu nome"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Criando conta...' : 'Criar Conta'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Powered by branding */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-xs text-muted-foreground">
          powered by <span className="font-semibold text-primary">Digisend</span>
        </p>
        <p className="text-[10px] text-muted-foreground/70">
          Sistema de Cobrança Inteligente
        </p>
      </div>
    </div>
  );
};

export default Auth;
